<template>
  <Head :title="`${t('page.title')} - ${assignment.name} - ${course.code}`" />
  <s-page-block size="xs" class="flex flex-col gap-3">
    <s-page-header :overline="t('page.title')" :heading="assignment.name" />

    <s-message color="red" icon="alert-outline" class="my-1">
      <p class="text-lg font-medium">
        {{ t('prerequisiteNotMet') }}
      </p>
    </s-message>

    <p class="text-lg">
      <i18n-t keypath="prerequisiteInfo">
        <template v-slot:prerequisiteName>
          <strong class="font-bold">{{ precondition.prerequisiteAssignment.name }}</strong>
        </template>
        <template v-slot:prerequisiteGrade>
          <strong class="font-bold">{{ precondition.minimumGradePercentage }}{{ $t('%') }}</strong>
        </template>
      </i18n-t>
    </p>

    <div class="flex-1 flex flex-wrap items-start justify-between gap-3 pt-1">
      <s-btn
        class="flex-1 w-full"
        color="white"
        icon="close"
        :href="route('courses.assignments.index', [course.id])"
      >
        {{ t('cancel') }}
      </s-btn>
      <s-btn
        :href="
          route('courses.assignments.show', [course.id, precondition.prerequisiteAssignmentId])
        "
        class="flex-1 justify-self-end"
        icon="arrow-right"
        iconEnd
      >
        {{ t('actions.viewPrerequisite') }}
      </s-btn>
    </div>
  </s-page-block>
</template>

<script setup lang="ts">
import {useI18n} from 'vue-i18n';
import {Head} from '@inertiajs/vue3';
import SPageBlock from '../../design-system/SPageBlock.vue';
import SPageHeader from '../../design-system/SPageHeader.vue';
import SMessage from '../../design-system/SMessage.vue';
import SBtn from '../../design-system/SBtn.vue';
import {useBreadcrumbs} from '../../composables/useBreadcrumbs';
import {route} from 'ziggy-js';
import {Assignment} from '../../types/entities/assignment';
import {Course} from '../../types/entities/course';
import AssignmentPreconditionDto = App.DTOs.AssignmentPreconditionDto;

const props = defineProps<{
  course: Course;
  assignment: Assignment;
  precondition: AssignmentPreconditionDto;
}>();

const {t} = useI18n({
  inheritLocale: true,
  scope: 'local',
});

useBreadcrumbs([
  {
    label: props.course.code,
    href: `/courses/${props.course.id}`,
    external: true,
  },
  {
    label: t('assignments'),
    href: route('courses.assignments.index', [props.course.id]),
  },
  {
    label: props.assignment.name,
    href: `/courses/${props.course.id}/assignments/${props.assignment.id}/overview`,
    external: true,
  },
]);
</script>

<i18n>
{
  "en": {
    "%": "%",
    "page": {
      "title": "Prerequisite Not Met"
    },
    "prerequisiteNotMet": "You have not met a required prerequisite for this assignment.",
    "prerequisiteInfo": "You need to complete {prerequisiteName} with a grade of at least {prerequisiteGrade} before you can start this assignment.",
    "actions": {
      "viewPrerequisite": "View Prerequisite"
    }
  },
  "fr": {
    "%": "%",
    "page": {
      "title": "Prérequis non satisfait"
    },
    "prerequisiteNotMet": "Vous n’avez pas satisfait à une condition préalable requise pour cette tâche.",
    "prerequisiteInfo": "Vous devez satisfaire à {prerequisiteName} avec une note d'au moins {prerequisiteGrade} avant de pouvoir commencer ce devoir.",
    "actions": {
      "viewPrerequisite": "Voir le prérequis"
    }
  }
}
</i18n>
